import { styled } from "@mui/material/styles";
import {
    Box,
    Dialog,
    DialogContent,
    DialogProps,
    Grid,
    IconButton,
    InternalStandardProps,
} from "@mui/material";
import { AppButton } from "../AppButton";
import { AppTypography } from "../AppTypography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppCircularProgress } from "../Progress/AppCircularProgress";
import { RenderIf } from "../Wrapper/RenderIf";

const StyledDialog = styled(Dialog)(() => ({}));

const WrapperDialog = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
}));

const StyledIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    cursor: "pointer",
    color: theme.palette.grey[600],
    fontSize: "22px",
}));

const Content = styled("div")(() => ({
    position: "relative",
    minHeight: "150px",
    display: "grid",
}));

const SendToMemberBtn = styled(AppButton)(() => ({
    height: "35px",
}));

const WrapperBtnSend = styled(Grid)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
}));

const SendIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    cursor: "pointer",
    color: theme.palette.grey[600],
    fontSize: "22px",
    marginRight: theme.spacing(2),
}));

const MembersWrapper = styled("span")(({ theme }) => ({
    background: theme.palette.grey[300],
    color: theme.palette.grey[600],
    borderRadius: "50%",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.5),
    minWidth: "25px",
    minHeight: "25px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "auto",
}));

const OkayButton = styled(AppButton)(() => ({
    width: "25%",
    padding: "0",
    height: "35px",
}));

const Grey600Typography = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

const BoldText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
    fontWeight: "bold",
}));

const LeftAlignText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
    textAlign: "left",
}));

export interface TransactionCompleteDialogProps
    extends InternalStandardProps<DialogProps> {
    onClose: () => void;
    onSendContent?: () => void;
    quantityLoading?: boolean;
    activeGiftItem?: {
        title: string;
        giftPurchasedEnabled: boolean;
        quantity: number;
        giftPurchasedSuccess: boolean;
    };
    selectedMembers?: any[];
    dialogMessage?: string;
    dialogTitle?: string;
    isGenericDialog?: boolean;
}

export const TransactionCompleteDialog = (
    props: TransactionCompleteDialogProps,
) => {
    const { enqueueSnackbar } = useSnackbar();
    const {
        className,
        selectedMembers,
        onClose,
        activeGiftItem,
        onSendContent,
        quantityLoading,
        dialogMessage,
        dialogTitle,
        isGenericDialog,
        ...rest
    } = props;

    const handleOnSendContent = () => {
        if (activeGiftItem?.title === "Member") {
            onSendContent();
        } else {
            enqueueSnackbar(messaging.common.featureComingSoon, {
                variant: "info",
            });
        }
    };

    return (
        <StyledDialog
            role="dialog"
            maxWidth={"sm"}
            fullWidth
            className={className}
            {...rest}
        >
            <WrapperDialog>
                <DialogContent>
                    <Content>
                        <RenderIf
                            isTrue={isGenericDialog}
                            fallback={
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignContent="baseline"
                                >
                                    <IconButton
                                        aria-label="Close"
                                        onClick={onClose}
                                    >
                                        <StyledIcon icon={["fas", "xmark"]} />
                                    </IconButton>
                                </Box>
                            }
                        >
                            <section role="presentation">
                                <BoldText
                                    alignSelf={"center"}
                                    textAlign={"center"}
                                    justifySelf={"center"}
                                    variant="body1"
                                >
                                    {dialogTitle}
                                </BoldText>
                                <LeftAlignText
                                    alignSelf={"center"}
                                    textAlign={"center"}
                                    justifySelf={"center"}
                                    marginTop={"5%"}
                                    variant="body1"
                                >
                                    {dialogMessage}
                                </LeftAlignText>
                            </section>
                        </RenderIf>

                        {activeGiftItem?.giftPurchasedSuccess && (
                            <section role="presentation">
                                <Grey600Typography
                                    alignSelf={"center"}
                                    textAlign={"center"}
                                    justifySelf={"center"}
                                    variant="body1"
                                >
                                    {messaging.gifts.successTitle}
                                </Grey600Typography>
                                <Grey600Typography
                                    alignSelf={"center"}
                                    textAlign={"center"}
                                    justifySelf={"center"}
                                    marginTop={"5%"}
                                    variant="body1"
                                >
                                    {messaging.gifts.successMsg(
                                        selectedMembers?.length,
                                    )}
                                </Grey600Typography>
                            </section>
                        )}

                        <RenderIf isTrue={isGenericDialog}>
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                alignContent="baseline"
                            >
                                <OkayButton onClick={onClose}>OK</OkayButton>
                            </Box>
                        </RenderIf>
                        {activeGiftItem?.giftPurchasedEnabled &&
                            !activeGiftItem?.giftPurchasedSuccess && (
                                <>
                                    <Grey600Typography
                                        alignSelf={"center"}
                                        textAlign={"center"}
                                        justifySelf={"center"}
                                        variant="body2"
                                    >
                                        Click the button below to send purchase
                                        content.
                                    </Grey600Typography>
                                    <WrapperBtnSend container>
                                        <WrapperBtnSend item xs={10} md={6}>
                                            {quantityLoading ? (
                                                <AppCircularProgress loaderSize="small" />
                                            ) : (
                                                <SendToMemberBtn
                                                    onClick={
                                                        handleOnSendContent
                                                    }
                                                    rounded
                                                    buttonVariant="grey-text"
                                                >
                                                    <SendIcon
                                                        icon={[
                                                            "far",
                                                            "paper-plane",
                                                        ]}
                                                    />
                                                    Send to{" "}
                                                    {activeGiftItem?.title}s
                                                </SendToMemberBtn>
                                            )}
                                            {selectedMembers &&
                                                selectedMembers.length > 0 && (
                                                    <MembersWrapper>
                                                        {selectedMembers.length}
                                                    </MembersWrapper>
                                                )}
                                        </WrapperBtnSend>
                                    </WrapperBtnSend>
                                </>
                            )}
                    </Content>
                </DialogContent>
            </WrapperDialog>
        </StyledDialog>
    );
};
